/**
 * Laravel Validation to Yup Schema Converter
 *
 * Converts Laravel validation rules to Yup validation schema
 * for use with form validation libraries.
 */

import * as yup from 'yup';

export type LaravelRule = string;
export type LaravelRules = LaravelRule | LaravelRule[];

interface FieldRules {
    [fieldName: string]: LaravelRules;
}

interface YupSchema {
    [fieldName: string]: yup.AnySchema;
}

/**
 * Safely parse a date string, handling special cases like 'today'
 */
function parseDate(dateString: string): Date {
    const lowerCaseDate = dateString.toLowerCase().trim();
    
    // Handle special cases
    switch (lowerCaseDate) {
        case 'today':
            return new Date();
        case 'yesterday':
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return yesterday;
        case 'tomorrow':
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow;
        default:
            // Try to parse the date
            const date = new Date(dateString);
            
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                throw new Error(`Invalid date: ${dateString}`);
            }
            
            return date;
    }
}

/**
 * Parse Laravel validation rule string into rule name and parameters
 * Example: "min:3" => { rule: "min", params: ["3"] }
 */
function parseRule(ruleString: string): { rule: string; params: string[] } {
    const parts = ruleString.split(':');
    const rule = parts[0].toLowerCase();
    const params = parts.length > 1 ? parts.slice(1).join(':').split(',') : [];
    return { rule, params };
}

/**
 * Normalize Laravel rules to array format
 */
function normalizeRules(rules: LaravelRules): string[] {
    if (typeof rules === 'string') {
        return rules.split('|').map(r => r.trim());
    }
    return rules;
}

/**
 * Convert a single field's Laravel rules to a Yup schema
 */
export function convertFieldToYup(
    fieldName: string,
    laravelRules: LaravelRules,
    customMessages?: Record<string, string>
): yup.AnySchema {
    const rules = normalizeRules(laravelRules);

    // Determine base type
    let schema: yup.AnySchema;

    // Check for type hints and modifiers
    if (rules.includes('string') || rules.includes('email') || rules.includes('url') || rules.includes('uuid')) {
        schema = yup.string();
    } else if (rules.includes('numeric') || rules.includes('integer')) {
        schema = yup.number();
    } else if (rules.includes('array')) {
        schema = yup.array();
    } else if (rules.includes('date')) {
        schema = yup.date();
    } else if (rules.includes('boolean')) {
        schema = yup.boolean();
    } else {
        // Default to mixed for unknown types
        schema = yup.mixed();
    }

    // Apply rules
    for (const ruleString of rules) {
        const { rule, params } = parseRule(ruleString);

        switch (rule) {
            // General & Required
            case 'required':
                schema = schema.required(customMessages?.required || `${fieldName} is required`);
                break;

            case 'nullable':
                schema = schema.nullable();
                break;

            case 'sometimes':
                schema = schema.optional();
                break;

            case 'filled':
                // Field must not be empty if present
                schema = schema.when(fieldName, {
                    is: (val: any) => val !== undefined,
                    then: (s) => s.required(customMessages?.filled || `${fieldName} must not be empty if present`),
                    otherwise: (s) => s.optional()
                });
                break;

            // String & Text Rules
            case 'string':
                // Already handled in base type
                break;

            case 'email':
                schema = (schema as yup.StringSchema).email(customMessages?.email || 'Must be a valid email');
                break;

            case 'min':
                const minVal = parseInt(params[0]);
                if (schema instanceof yup.StringSchema) {
                    schema = schema.min(minVal, customMessages?.min || `Must be at least ${minVal} characters`);
                } else if (schema instanceof yup.NumberSchema) {
                    schema = schema.min(minVal, customMessages?.min || `Must be at least ${minVal}`);
                } else if (schema instanceof yup.ArraySchema) {
                    schema = schema.min(minVal, customMessages?.min || `Must have at least ${minVal} items`);
                }
                break;

            case 'max':
                const maxVal = parseInt(params[0]);
                if (schema instanceof yup.StringSchema) {
                    schema = schema.max(maxVal, customMessages?.max || `Must be at most ${maxVal} characters`);
                } else if (schema instanceof yup.NumberSchema) {
                    schema = schema.max(maxVal, customMessages?.max || `Must be at most ${maxVal}`);
                } else if (schema instanceof yup.ArraySchema) {
                    schema = schema.max(maxVal, customMessages?.max || `Must have at most ${maxVal} items`);
                }
                break;

            case 'between':
                const [betweenMin, betweenMax] = params.map(p => parseInt(p));
                if (schema instanceof yup.StringSchema) {
                    schema = schema.min(betweenMin).max(betweenMax);
                } else if (schema instanceof yup.NumberSchema) {
                    schema = schema.min(betweenMin).max(betweenMax);
                }
                break;

            case 'confirmed':
                // Requires matching another field
                const confirmField = params[0] || `${fieldName}_confirmation`;
                schema = (schema as yup.StringSchema).oneOf(
                    [yup.ref(confirmField)],
                    customMessages?.confirmed || 'Fields must match'
                );
                break;

            case 'regex':
                const pattern = params[0];
                schema = (schema as yup.StringSchema).matches(
                    new RegExp(pattern),
                    customMessages?.regex || 'Format is invalid'
                );
                break;

            case 'in':
                schema = schema.oneOf(params, customMessages?.in || `Must be one of: ${params.join(', ')}`);
                break;

            case 'not_in':
                schema = schema.notOneOf(params, customMessages?.not_in || `Must not be one of: ${params.join(', ')}`);
                break;

            case 'starts_with':
                schema = (schema as yup.StringSchema).test(
                    'starts-with',
                    customMessages?.starts_with || `Must start with: ${params.join(', ')}`,
                    (value) => {
                        if (!value) return true;
                        return params.some(prefix => value.startsWith(prefix));
                    }
                );
                break;

            case 'ends_with':
                schema = (schema as yup.StringSchema).test(
                    'ends-with',
                    customMessages?.ends_with || `Must end with: ${params.join(', ')}`,
                    (value) => {
                        if (!value) return true;
                        return params.some(suffix => value.endsWith(suffix));
                    }
                );
                break;

            // Numeric & Integer Rules
            case 'numeric':
                // Already handled in base type
                break;

            case 'integer':
                schema = (schema as yup.NumberSchema).integer(customMessages?.integer || 'Must be an integer');
                break;

            case 'gt':
                const gtVal = parseFloat(params[0]);
                schema = (schema as yup.NumberSchema).moreThan(gtVal, customMessages?.gt || `Must be greater than ${gtVal}`);
                break;

            case 'gte':
                const gteVal = parseFloat(params[0]);
                schema = (schema as yup.NumberSchema).min(gteVal, customMessages?.gte || `Must be at least ${gteVal}`);
                break;

            case 'lt':
                const ltVal = parseFloat(params[0]);
                schema = (schema as yup.NumberSchema).lessThan(ltVal, customMessages?.lt || `Must be less than ${ltVal}`);
                break;

            case 'lte':
                const lteVal = parseFloat(params[0]);
                schema = (schema as yup.NumberSchema).max(lteVal, customMessages?.lte || `Must be at most ${lteVal}`);
                break;

            // Date & Time Rules
            case 'date':
                // Already handled in base type
                break;

            case 'before':
                const beforeDate = params[0];
                try {
                    const parsedBeforeDate = parseDate(beforeDate);
                    schema = (schema as yup.DateSchema).max(
                        parsedBeforeDate,
                        customMessages?.before || `Must be before ${beforeDate}`
                    );
                } catch (error) {
                    console.warn(`Invalid before date: ${beforeDate}. Skipping validation.`);
                }
                break;

            case 'after':
                const afterDate = params[0];
                try {
                    const parsedAfterDate = parseDate(afterDate);
                    schema = (schema as yup.DateSchema).min(
                        parsedAfterDate,
                        customMessages?.after || `Must be after ${afterDate}`
                    );
                } catch (error) {
                    console.warn(`Invalid after date: ${afterDate}. Skipping validation.`);
                }
                break;

            case 'date_format':
                // Yup doesn't have built-in date format validation
                // You might want to use a custom test with a date parsing library
                const dateFormat = params[0];
                schema = (schema as yup.DateSchema).test(
                    'date-format',
                    customMessages?.date_format || `Date must be in format: ${dateFormat}`,
                    (value) => {
                        if (!value) return true;
                        // Custom validation logic here based on format
                        return true; // Placeholder
                    }
                );
                break;

            // Array & Object Rules
            case 'array':
                // Already handled in base type
                break;

            case 'distinct':
                schema = (schema as yup.ArraySchema<any, any>).test(
                    'distinct',
                    customMessages?.distinct || 'All items must be unique',
                    (value: any) => {
                        if (!Array.isArray(value)) return true;
                        return new Set(value).size === value.length;
                    }
                );
                break;

            case 'size':
                const size = parseInt(params[0]);
                if (schema instanceof yup.StringSchema) {
                    schema = schema.length(size, customMessages?.size || `Must be exactly ${size} characters`);
                } else if (schema instanceof yup.ArraySchema) {
                    schema = schema.length(size, customMessages?.size || `Must have exactly ${size} items`);
                } else if (schema instanceof yup.NumberSchema) {
                    schema = schema.test(
                        'size',
                        customMessages?.size || `Must equal ${size}`,
                        (value) => value === size
                    );
                }
                break;

            // Network & URL Rules
            case 'url':
                schema = (schema as yup.StringSchema).url(customMessages?.url || 'Must be a valid URL');
                break;

            case 'ip':
            case 'ipv4':
                schema = (schema as yup.StringSchema).test(
                    'ipv4',
                    customMessages?.ip || 'Must be a valid IPv4 address',
                    (value) => {
                        if (!value) return true;
                        const ipv4Regex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
                        return ipv4Regex.test(value);
                    }
                );
                break;

            case 'ipv6':
                schema = (schema as yup.StringSchema).test(
                    'ipv6',
                    customMessages?.ipv6 || 'Must be a valid IPv6 address',
                    (value) => {
                        if (!value) return true;
                        const ipv6Regex = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
                        return ipv6Regex.test(value);
                    }
                );
                break;

            case 'uuid':
                schema = (schema as yup.StringSchema).uuid(customMessages?.uuid || 'Must be a valid UUID');
                break;

            case 'boolean':
                // Already handled in base type
                break;

            default:
                console.warn(`Unknown Laravel validation rule: ${rule}`);
        }
    }

    return schema;
}

/**
 * Convert Laravel validation rules object to Yup schema
 *
 * @param rules - Object with field names as keys and Laravel rules as values
 * @param customMessages - Optional custom error messages per field
 * @returns Yup object schema
 */
export function laravelToYup(
    rules: FieldRules,
    customMessages?: Record<string, Record<string, string>>
): yup.ObjectSchema<any> {
    const schemaShape: YupSchema = {};

    for (const [fieldName, fieldRules] of Object.entries(rules)) {
        const messages = customMessages?.[fieldName];
        schemaShape[fieldName] = convertFieldToYup(fieldName, fieldRules, messages);
    }

    return yup.object().shape(schemaShape);
}

/**
 * Utility to create Yup schema from Laravel-style rule string
 *
 * @example
 * const schema = createYupSchema('email', 'required|email|max:255');
 */
export function createYupSchema(fieldName: string, rules: LaravelRules): yup.AnySchema {
    return convertFieldToYup(fieldName, rules);
}

/**
 * Helper to validate data against Laravel-style rules
 *
 * @example
 * const isValid = await validateWithLaravelRules(
 *   { email: 'test@example.com', age: 25 },
 *   { email: 'required|email', age: 'required|numeric|min:18' }
 * );
 */
export async function validateWithLaravelRules(
    data: Record<string, any>,
    rules: FieldRules
): Promise<{ isValid: boolean; errors?: Record<string, string> }> {
    const schema = laravelToYup(rules);

    try {
        await schema.validate(data, { abortEarly: false });
        return { isValid: true };
    } catch (err) {
        if (err instanceof yup.ValidationError) {
            const errors: Record<string, string> = {};
            err.inner.forEach((error) => {
                if (error.path) {
                    errors[error.path] = error.message;
                }
            });
            return { isValid: false, errors };
        }
        throw err;
    }
}

export default laravelToYup;
